/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Button from "@mui/material/Button";
import SoftButton from "components/SoftButton";

const createItem = () => ({
  documentNumber: "",
  documentDate: "",
  description: "",
  exemptedAmount: "",
  alreadyExemptedAmount: "",
});

const defaultItems = [createItem(), createItem()];

function DetailLedgerVatExemptionReductionDetail({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();

  // --- QUẢN LÝ STATE ---
  const [headerInfo, setHeaderInfo] = useState({
    company: "",
    companyAddress: "",
    year: today.getFullYear().toString(),
    totalPages: "",
    openingDate: "",
    signingDay: today.getDate().toString(),
    signingMonth: (today.getMonth() + 1).toString(),
    signingYear: today.getFullYear().toString(),
    preparerName: "",
    chiefAccountantName: "",
    legalRepresentativeName: "",
  });

  const [openingBalance, setOpeningBalance] = useState({
    openingExemptedAmount: "",
    openingAlreadyExemptedAmount: "",
  });

  const [localItems, setLocalItems] = useState(defaultItems);

  // Sync state with formik values
  useEffect(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      setLocalItems(values.items);
    }
    setOpeningBalance({
      openingExemptedAmount: values.openingExemptedAmount || "",
      openingAlreadyExemptedAmount: values.openingAlreadyExemptedAmount || "",
    });
    setHeaderInfo((prev) => ({ ...prev, ...values }));
  }, [values]);

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  const handleOpeningBalanceChange = (field, val) => {
    setOpeningBalance((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) onChangeValue(field, val);
  };

  const handleChangeItem = (index, field, value) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLocalItems(newItems);
  };

  const addRow = () => setLocalItems([...localItems, createItem()]);

  const removeRow = (index) => {
    if (localItems.length <= 1) return;
    setLocalItems(localItems.filter((_, i) => i !== index));
  };

  // --- LOGIC TÍNH TOÁN (TỰ ĐỘNG) ---
  const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

  const calculatedTotal = useMemo(() => {
    return {
      totalArisingExemptedAmount: localItems.reduce(
        (acc, item) => acc + num(item.exemptedAmount),
        0
      ),
      totalArisingAlreadyExemptedAmount: localItems.reduce(
        (acc, item) => acc + num(item.alreadyExemptedAmount),
        0
      ),
    };
  }, [localItems]);

  const calculatedClosing = useMemo(() => {
    return {
      closingExemptedAmount:
        num(openingBalance.openingExemptedAmount) + calculatedTotal.totalArisingExemptedAmount,
      closingAlreadyExemptedAmount:
        num(openingBalance.openingAlreadyExemptedAmount) +
        calculatedTotal.totalArisingAlreadyExemptedAmount,
    };
  }, [openingBalance, calculatedTotal]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("totalArisingExemptedAmount", calculatedTotal.totalArisingExemptedAmount);
      onChangeValue(
        "totalArisingAlreadyExemptedAmount",
        calculatedTotal.totalArisingAlreadyExemptedAmount
      );
      onChangeValue("closingExemptedAmount", calculatedClosing.closingExemptedAmount);
      onChangeValue("closingAlreadyExemptedAmount", calculatedClosing.closingAlreadyExemptedAmount);
    }
  }, [localItems, calculatedTotal, calculatedClosing]);

  // --- STYLES ---
  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "40px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "20px 0" },
    titleMain: { fontSize: "18px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "14px", marginTop: "15px" },
    th: {
      border: "1px solid #000",
      padding: "8px 4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
    },
    td: { border: "1px solid #000", padding: 0, height: "35px" },
    tdText: { border: "1px solid #000", padding: "0 8px", height: "35px", verticalAlign: "middle" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "14px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 8px",
      display: "block",
      color: "#000",
    },
    inputTableCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: "14px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "14px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 8px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 8px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "35px",
    },
    rowBg: { backgroundColor: "#fafafa", fontWeight: "bold" },
    removeBtn: {
      border: "none",
      background: "transparent",
      color: "red",
      fontWeight: "bold",
      cursor: "pointer",
      width: "100%",
      height: "100%",
    },
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "45%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "250px", fontWeight: "bold" }}
              value={headerInfo.company}
              onChange={(e) => handleHeaderChange("company", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Địa chỉ:{" "}
            <input
              disabled={containView}
              style={{ ...s.inputDotted, width: "250px", fontWeight: "bold" }}
              value={headerInfo.companyAddress}
              onChange={(e) => handleHeaderChange("companyAddress", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "55%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S63-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>SỔ CHI TIẾT THUẾ GIÁ TRỊ GIA TĂNG ĐƯỢC MIỄN GIẢM</h2>
        <div style={{ marginTop: 10, fontWeight: "bold" }}>
          Năm:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "100px", fontWeight: "bold", textAlign: "center" }}
            value={headerInfo.year}
            onChange={(e) => handleHeaderChange("year", e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th colSpan={2} style={{ ...s.th, width: "20%" }}>
                Chứng từ
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "40%" }}>
                Diễn giải
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "20%" }}>
                Số thuế GTGT được
                <br />
                miễn giảm
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "20%" }}>
                Số thuế GTGT đã
                <br />
                miễn giảm
              </th>
              {!containView && (
                <th rowSpan={3} style={{ ...s.th, width: "40px" }}>
                  #
                </th>
              )}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "10%" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "10%" }}>Ngày, tháng</th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
            </tr>
          </thead>
          <tbody>
            {/* SỐ DƯ ĐẦU KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.tdText}>- Số dư đầu kỳ</td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.openingExemptedAmount}
                  onChange={(e) =>
                    handleOpeningBalanceChange("openingExemptedAmount", e.target.value)
                  }
                />
              </td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTable, fontWeight: "bold" }}
                  value={openingBalance.openingAlreadyExemptedAmount}
                  onChange={(e) =>
                    handleOpeningBalanceChange("openingAlreadyExemptedAmount", e.target.value)
                  }
                />
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* LABEL SỐ PHÁT SINH */}
            <tr>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.tdText, fontWeight: "bold" }}>- Số phát sinh trong kỳ:</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* DÒNG PHÁT SINH (ITEMS) */}
            {localItems.map((row, i) => (
              <tr key={i}>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentNumber || ""}
                    onChange={(e) => handleChangeItem(i, "documentNumber", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableCenter}
                    value={row.documentDate || ""}
                    onChange={(e) => handleChangeItem(i, "documentDate", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    style={s.inputTableLeft}
                    value={row.description || ""}
                    onChange={(e) => handleChangeItem(i, "description", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.exemptedAmount || ""}
                    onChange={(e) => handleChangeItem(i, "exemptedAmount", e.target.value)}
                  />
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTable}
                    value={row.alreadyExemptedAmount || ""}
                    onChange={(e) => handleChangeItem(i, "alreadyExemptedAmount", e.target.value)}
                  />
                </td>
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeRow(i)}>
                      X
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {/* CỘNG SỐ PHÁT SINH */}
            <tr style={s.rowBg}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.tdText}>- Cộng số phát sinh</td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedTotal.totalArisingExemptedAmount)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedTotal.totalArisingAlreadyExemptedAmount)}
                </span>
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* SỐ DƯ CUỐI KỲ */}
            <tr style={s.rowBg}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.tdText}>- Số dư cuối kỳ</td>
              <td style={s.td}>
                <span style={s.totalText}>{fmt(calculatedClosing.closingExemptedAmount)}</span>
              </td>
              <td style={s.td}>
                <span style={s.totalText}>
                  {fmt(calculatedClosing.closingAlreadyExemptedAmount)}
                </span>
              </td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>
        {!containView && (
          <SoftBox mt={3} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addRow}>
              Thêm dòng
            </SoftButton>
            {/* <Button variant="outlined" color="dark" onClick={addRow} sx={{ mr: 2 }}>
              + Thêm dòng phát sinh
            </Button> */}
          </SoftBox>
        )}
      </div>

      {/* FOOTER INFO (Sổ/Trang) */}
      <div style={{ marginTop: "20px" }}>
        <div style={{ marginBottom: "5px" }}>
          - Sổ này có{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "50px" }}
            value={headerInfo.totalPages}
            onChange={(e) => handleHeaderChange("totalPages", e.target.value)}
          />{" "}
          trang, đánh số từ trang 01 đến trang...
        </div>
        <div>
          - Ngày mở sổ:{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px" }}
            value={headerInfo.openingDate}
            onChange={(e) => handleHeaderChange("openingDate", e.target.value)}
          />
        </div>
      </div>

      {/* FOOTER CHỮ KÝ */}
      {/* <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}></div>
          <b>Người ghi sổ</b>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.preparerName}
            onChange={(e) => handleHeaderChange("preparerName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}></div>
          <b>Kế toán trưởng</b>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginBottom: "65px" }}>
            (Ký, họ tên)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.chiefAccountantName}
            onChange={(e) => handleHeaderChange("chiefAccountantName", e.target.value)}
          />
        </div>
        <div style={{ width: "33%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingDay}
              onChange={(e) => handleHeaderChange("signingDay", e.target.value)}
            />
            tháng
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, marginLeft: 4 }}
              value={headerInfo.signingMonth}
              onChange={(e) => handleHeaderChange("signingMonth", e.target.value)}
            />
            năm
            <input
              disabled={containView}
              style={{ ...s.inputDottedSmall, width: "50px", marginLeft: 4 }}
              value={headerInfo.signingYear}
              onChange={(e) => handleHeaderChange("signingYear", e.target.value)}
            />
          </div>
          <b>Người đại diện theo pháp luật</b>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginBottom: "45px" }}>
            (Ký, họ tên, đóng dấu)
          </div>
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.legalRepresentativeName}
            onChange={(e) => handleHeaderChange("legalRepresentativeName", e.target.value)}
          />
        </div>
      </div> */}
    </SoftBox>
  );
}

DetailLedgerVatExemptionReductionDetail.propTypes = {
  formData: PropTypes.object,
};

export default DetailLedgerVatExemptionReductionDetail;
